import type { Identifier } from "ra-core";

import type { Comercial } from "./tipos";

/**
 * Equipo comercial (S9-S10, Jan y Robert 06-07/10/2026): filtro por persona del Pipeline y
 * la Agenda de los administradores, reparto por % y tasas del embudo de cada setter.
 */

/** «todas», «sin_asignar» o el id (texto) de una persona. */
export type FiltroPersona = string;

export const TODAS = "todas";
export const SIN_ASIGNAR = "sin_asignar";

/**
 * Filtro por persona con selección múltiple (Robert, 13/10/2026) para Leads, Pipeline y Agenda
 * de los administradores: ids (texto) de personas y, si se quiere, SIN_ASIGNAR. Vacío = todas.
 */
export type FiltroPersonas = string[];

/** Las fichas de cualquiera de las personas elegidas (y las sin asignar, si se eligió). */
export const filtrarPorPersonas = <
  T extends { asignado_a?: Identifier | null },
>(
  lista: T[],
  seleccion: FiltroPersonas,
): T[] => {
  if (!seleccion.length) return lista;
  const ids = new Set(seleccion.filter((s) => s !== SIN_ASIGNAR));
  const sinAsignar = seleccion.includes(SIN_ASIGNAR);
  return lista.filter((n) =>
    n.asignado_a == null ? sinAsignar : ids.has(String(n.asignado_a)),
  );
};

/**
 * La selección como filtro de PostgREST sobre `asignado_a`: `in`, `is null` o, si son personas y
 * «sin asignar» a la vez, una condición «o» (`expresion`, en sintaxis de PostgREST).
 */
export const condicionPersonas = (
  seleccion: FiltroPersonas,
):
  | { tipo: "nada" }
  | { tipo: "simple"; filtro: Record<string, unknown> }
  | { tipo: "o"; expresion: string } => {
  const ids = seleccion
    .filter((s) => s !== SIN_ASIGNAR)
    .filter((s) => /^\d+$/.test(s));
  const sinAsignar = seleccion.includes(SIN_ASIGNAR);
  if (!ids.length && !sinAsignar) return { tipo: "nada" };
  if (!ids.length) return { tipo: "simple", filtro: { "asignado_a@is": null } };
  const lista = `(${ids.join(",")})`;
  if (!sinAsignar)
    return { tipo: "simple", filtro: { "asignado_a@in": lista } };
  return { tipo: "o", expresion: `asignado_a.in.${lista},asignado_a.is.null` };
};

/** Las fichas de esa persona (o sin asignar, o todas). */
export const filtrarPorPersona = <T extends { asignado_a?: Identifier | null }>(
  lista: T[],
  filtro: FiltroPersona,
): T[] => {
  if (!filtro || filtro === TODAS) return lista;
  if (filtro === SIN_ASIGNAR) return lista.filter((n) => n.asignado_a == null);
  return lista.filter((n) => String(n.asignado_a ?? "") === filtro);
};

/**
 * Suma de los % de reparto de quien recibe leads (activos). El reparto es proporcional a
 * esos pesos, así que no tiene por qué dar 100: la pantalla Equipo avisa si no lo da.
 */
export const sumaPorcentajes = (
  comerciales: Pick<
    Comercial,
    "recibe_leads" | "porcentaje_reparto" | "disabled"
  >[],
): number =>
  comerciales
    .filter((c) => c.recibe_leads && !c.disabled)
    .reduce((s, c) => s + Number(c.porcentaje_reparto ?? 0), 0);

/** Tasa de paso entre dos pasos del embudo, en % entero; null si no hay base. */
export const tasa = (de: number, a: number): number | null =>
  de > 0 ? Math.round((a / de) * 100) : null;

/** 40: suma del equipo (todas las columnas numéricas), para comparar a cada setter con el total. */
export const sumarMetricas = <T extends object>(
  filas: T[],
): Omit<T, "sale_id"> => {
  const total: Record<string, number> = {};
  for (const f of filas) {
    for (const [k, v] of Object.entries(f)) {
      if (k === "sale_id" || v == null) continue;
      const n = Number(v);
      if (Number.isNaN(n)) continue;
      total[k] = (total[k] ?? 0) + n;
    }
  }
  return total as Omit<T, "sale_id">;
};

/** 40: diferencia mínima (en puntos de %) para señalar un paso como el más flojo. */
export const DIFERENCIA_PASO_FLOJO = 10;

/**
 * 40 (Robert, 23/10/2026): el paso del embudo donde la tasa de una persona más cae por debajo de la
 * del equipo (al menos DIFERENCIA_PASO_FLOJO puntos); null si en ninguno. Es lo que conviene entrenar.
 */
export const pasoMasFlojo = <P extends string>(
  m: Record<P, unknown>,
  equipo: Record<P, unknown>,
  pasos: readonly P[],
): P | null => {
  let peor: P | null = null;
  let peorDiferencia = DIFERENCIA_PASO_FLOJO - 1;
  for (let i = 1; i < pasos.length; i++) {
    const suya = tasa(Number(m[pasos[i - 1]]), Number(m[pasos[i]]));
    const delEquipo = tasa(
      Number(equipo[pasos[i - 1]]),
      Number(equipo[pasos[i]]),
    );
    if (suya == null || delEquipo == null) continue;
    if (delEquipo - suya > peorDiferencia) {
      peorDiferencia = delEquipo - suya;
      peor = pasos[i];
    }
  }
  return peor;
};

/** Primer y último instante (ISO) de un mes «aaaa-mm», en hora local. */
export const rangoMes = (mes: string): { desde: string; hasta: string } => {
  const [a, m] = mes.split("-").map(Number);
  const desde = new Date(a, m - 1, 1, 0, 0, 0, 0);
  const hasta = new Date(a, m, 1, 0, 0, 0, 0);
  return { desde: desde.toISOString(), hasta: hasta.toISOString() };
};

/**
 * Orden de «Sin asignar»: primero la prioridad de Prospect (1 antes que 3; sin prioridad,
 * al final) y, a igual prioridad, el que lleva más tiempo esperando.
 */
export const ordenSinAsignar = <
  T extends {
    prospect_prioridad?: number | null;
    recuperado_at?: string | null;
    prospect_entregado_at?: string | null;
    created_at: string;
  },
>(
  lista: T[],
): T[] => {
  const desde = (n: T) =>
    n.recuperado_at ?? n.prospect_entregado_at ?? n.created_at;
  return [...lista].sort(
    (a, b) =>
      (a.prospect_prioridad ?? 99) - (b.prospect_prioridad ?? 99) ||
      desde(a).localeCompare(desde(b)),
  );
};
